/**
 * @file ContractExtractionViewView.tsx
 * @description Pure presentation for the shared extraction-view atom.
 *   Renders one `CollapsibleTierHeader` per category with the matching
 *   `ExtractionDataSection` rendered beneath (filtered to that category)
 *   when the section is expanded. Optional Discard + Add Document
 *   slot affordances render only when the wrapper requests them AND
 *   the atom is not read-only.
 *
 *   Collapsed-section state is tracked by the stable raw `category.key`
 *   (quality #9 M2) so the formatter in
 *   `features/contracts/utils/formatCategoryDisplayName` can evolve
 *   (e.g. i18n) without resetting existing collapse state. The display
 *   name is still passed through to `CollapsibleTierHeader` as the
 *   visible label; only the bookkeeping key changed.
 *
 *   Consumers:
 *   - Desktop drawer body (`ContractDrawerExtractionBody`)
 *   - Mobile full-page contract screen (`ContractScreen`)
 *   - Phase 4 Player Page Financials tab (PR C)
 * @module features/contracts/components/ContractExtractionView/ContractExtractionViewView
 */

import { Trash2 } from "lucide-react-native";
import React, { memo } from "react";

import { CONTRACT_END_DATE_CATEGORY_KEY } from "@/features/contracts/constants";
import type { ContractLineItemMutations } from "@/features/contracts/hooks/useContractLineItemMutations";
import type { ContractExtraction } from "@/features/contracts/types";
import CollapsibleTierHeader from "@/features/squad-planning/components/molecules/CollapsibleTierHeader";
import ContractDrawerAddDocumentAction from "@/features/contracts/components/molecules/ContractDrawerAddDocumentAction";
import ContractEndDateSection from "@/features/contracts/components/molecules/ContractEndDateSection";
import ExtractionDataSection from "@/features/contracts/components/organisms/ExtractionDataSection";

import { Button } from "@/components/atoms/Button";
import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";

/** A parsed category for the collapsible render list. */
export interface ExtractionCategorySummary {
  readonly key: string;
  readonly displayName: string;
  readonly itemCount: number;
}

/** Props for {@link ContractExtractionViewView}. */
export interface ContractExtractionViewViewProps {
  /** Loaded extraction the view renders against. */
  readonly extraction: ContractExtraction;
  /** Resolved player UUID for forwarding to mutations. */
  readonly playerId: string;
  /** Document type for forwarding to mutations. */
  readonly documentType: string;
  /** Parsed categories in render order. */
  readonly categories: readonly ExtractionCategorySummary[];
  /**
   * Set of collapsed **category keys** (expanded when absent). Keyed by
   * `category.key` rather than `displayName` so i18n/formatter changes
   * cannot silently reset collapse state (quality #9 M2).
   */
  readonly collapsedCategories: ReadonlySet<string>;
  /**
   * Toggle handler — receives the stable raw `category.key` (NOT the
   * formatted display name). The CollapsibleTierHeader atom sends the
   * tierName back through its own callback, but we pre-bind each
   * category's press handler in the loop below so the payload we hand
   * to the Container is always the canonical `.key`. Taking `.key`
   * here (rather than the label) is what makes the collapse set
   * bookkeeping safe: the formatter can evolve (i18n, casing fixes)
   * without silently toggling the wrong section.
   */
  readonly onToggleTier: (categoryKey: string) => void;
  /**
   * When true, every editing affordance is suppressed. The underlying
   * `ExtractionDataSection` also receives `readOnly` so row-level
   * pencil/trash disappear.
   */
  readonly readOnly: boolean;
  /**
   * When true AND not readOnly, render the Discard button at the top
   * (drawer/page consumer). PR C Financials passes false.
   */
  readonly showDiscardButton: boolean;
  /**
   * When true AND not readOnly, render the "+ Add Document" action
   * at the bottom (drawer/page SAVED view). PR C Financials passes
   * false.
   */
  readonly showAddDocumentAction: boolean;
  /** Handler for the Discard button (required when showDiscardButton is true). */
  readonly onOpenDeleteDialog: () => void;
  /** Handler for "+ Add Document" (required when showAddDocumentAction is true). */
  readonly onAddDocumentPress: () => void;
  /**
   * Shared mutation bundle (perf #11 F1 / SE-4333 item N). Passed down
   * so every `ExtractionDataSection` in the category loop below uses
   * the same four Apollo mutation hooks, rather than each section
   * mounting its own. Optional so consumers that don't wire a parent
   * bundle still work — the section falls back to local hooks then.
   */
  readonly mutations?: ContractLineItemMutations;
}

/**
 * Pure presentation for the shared extraction atom.
 * @param props - View props
 * @param props.extraction - Loaded extraction
 * @param props.playerId - Resolved player UUID
 * @param props.documentType - Document type
 * @param props.categories - Parsed categories (ordered)
 * @param props.collapsedCategories - Set of collapsed category keys
 * @param props.onToggleTier - Handler invoked with the tier display name
 * @param props.readOnly - Gates every mutation affordance
 * @param props.showDiscardButton - Whether to render the Discard button
 * @param props.showAddDocumentAction - Whether to render the "+ Add Document" action
 * @param props.onOpenDeleteDialog - Discard handler
 * @param props.onAddDocumentPress - Add-document handler
 * @param props.mutations - Shared mutation bundle forwarded to every
 *   section (perf #11 F1)
 * @returns The rendered view
 */
const ContractExtractionViewView = ({
  extraction,
  playerId,
  documentType,
  categories,
  collapsedCategories,
  onToggleTier,
  readOnly,
  showDiscardButton,
  showAddDocumentAction,
  onOpenDeleteDialog,
  onAddDocumentPress,
  mutations,
}: ContractExtractionViewViewProps) => (
  <Box testID="contract-extraction-view">
    <Stack className="gap-3 p-4">
      {!readOnly && showDiscardButton ? (
        <Button
          testID="discard-extraction-btn"
          label="Discard"
          tone="danger"
          variant="outline"
          size="sm"
          icon={Trash2}
          onPress={onOpenDeleteDialog}
          className="self-start"
        />
      ) : null}

      {categories.map(category => {
        // SE-4881: the dedicated Contract End Date section is NOT a
        // collapsible `extractedData` category — short-circuit it to
        // `ContractEndDateSection`, which reads `extraction.endDate`
        // and owns its own inline edit. It is rendered FIRST because
        // `parseCategorySummaries` prepends its synthetic summary.
        if (category.key === CONTRACT_END_DATE_CATEGORY_KEY) {
          return (
            <ContractEndDateSection
              key={category.key}
              // Reuse the displayName already computed in
              // parseCategorySummaries ("Contract End Date") so the
              // section heading stays in lockstep with the category
              // formatter (AC1).
              label={category.displayName}
              extractionId={extraction.id}
              playerId={playerId}
              documentType={documentType}
              endDate={extraction.endDate ?? null}
              readOnly={readOnly}
            />
          );
        }
        // Key on the stable raw `category.key` so a formatter change
        // can't silently reset collapse state. See quality #9 M2.
        const isCollapsed = collapsedCategories.has(category.key);
        // Pre-bind the toggle to the stable `category.key` here so
        // the Container receives the raw key (not the formatted
        // label). CollapsibleTierHeader's `onToggleTier` contract
        // emits the tierName string, but we ignore that payload and
        // fire our canonical key through the parent callback.
        // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop -- stable reference not achievable without hoisting categories into a parent useMemo of pre-bound handlers; the per-render allocation is one closure per category which is negligible next to the tree render cost.
        const handleToggle = (_tierName: string) => onToggleTier(category.key);
        return (
          <Stack key={category.key} className="gap-0">
            <Box testID={`contract-drawer-category-header-${category.key}`}>
              <CollapsibleTierHeader
                tierName={category.displayName}
                count={category.itemCount}
                isCollapsed={isCollapsed}
                onToggleTier={handleToggle}
              />
            </Box>
            {isCollapsed ? null : (
              <Box className="rounded-b-sm border border-t-0 border-outline-default p-3">
                <ExtractionDataSection
                  extractionId={extraction.id}
                  extractedData={
                    (extraction.extractedData ?? {}) as Record<string, unknown>
                  }
                  playerId={playerId}
                  documentType={documentType}
                  readOnly={readOnly}
                  categoryFilter={category.key}
                  mutations={mutations}
                />
              </Box>
            )}
          </Stack>
        );
      })}

      {showAddDocumentAction ? (
        // SE-4347 AC-4 fix: in SAVED view mode the drawer body must
        // render the "+ Add Document" CTA even though `readOnly=true`
        // for the data atom (line items are locked behind the Edit
        // banner in that mode). `readOnly` here is a data-atom-only
        // signal — it suppresses per-row pencil/trash affordances.
        // The CTA gate is the caller's `showAddDocumentAction` flag,
        // which the body already computes correctly as
        // `isSavedViewMode && !isReadOnly`. Double-gating on
        // `!readOnly` here blocks the SAVED-mode CTA from ever
        // rendering. The PR C Player Page Financials caller passes
        // `showAddDocumentAction={false}` and therefore still gets
        // no CTA — the contract holds for every consumer.
        <ContractDrawerAddDocumentAction onPress={onAddDocumentPress} />
      ) : null}
    </Stack>
  </Box>
);

ContractExtractionViewView.displayName = "ContractExtractionViewView";

export default memo(ContractExtractionViewView);
